import { useMemo, useState } from 'react';
import { ChevronRight, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { CategoryToken } from '@sleed/bizcommerce-api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface TreeNode {
	id: number;
	name: string;
	children: TreeNode[];
}

interface FlatNode {
	node: TreeNode;
	level: number;
}

/** Build a tree from the flat `pid` list, ordered by SortIndex then name (mirrors the legacy TreeList). */
function buildTree(categories: CategoryToken[]): TreeNode[] {
	const byId = new Map<number, TreeNode>();
	for (const c of categories) if (c.id != null) byId.set(c.id, { id: c.id, name: c.nm ?? '', children: [] });

	const roots: TreeNode[] = [];
	const orderHint = new Map<number, number>();
	categories.forEach(c => c.id != null && orderHint.set(c.id, c.si ?? 0));

	for (const c of categories) {
		if (c.id == null) continue;
		const node = byId.get(c.id)!;
		const parent = c.pid != null ? byId.get(c.pid) : undefined;
		if (parent) parent.children.push(node);
		else roots.push(node);
	}

	const sortNodes = (nodes: TreeNode[]) => {
		nodes.sort((a, b) => (orderHint.get(a.id) ?? 0) - (orderHint.get(b.id) ?? 0) || a.name.localeCompare(b.name));
		nodes.forEach(n => sortNodes(n.children));
	};
	sortNodes(roots);
	return roots;
}

function flatten(nodes: TreeNode[], expanded: Set<number>, forceExpand: boolean, level: number, out: FlatNode[]) {
	for (const node of nodes) {
		out.push({ node, level });
		if (node.children.length > 0 && (forceExpand || expanded.has(node.id))) {
			flatten(node.children, expanded, forceExpand, level + 1, out);
		}
	}
}

function filterTree(nodes: TreeNode[], q: string): TreeNode[] {
	const result: TreeNode[] = [];
	for (const node of nodes) {
		const childMatches = filterTree(node.children, q);
		if (node.name.toLowerCase().includes(q) || childMatches.length > 0) {
			result.push({ ...node, children: childMatches });
		}
	}
	return result;
}

function collectParentIds(nodes: TreeNode[], out: number[]) {
	for (const node of nodes) {
		if (node.children.length > 0) {
			out.push(node.id);
			collectParentIds(node.children, out);
		}
	}
}

interface CategoryTreeProps {
	categories: CategoryToken[];
	loading: boolean;
	selectedId: number | null;
	onSelect: (id: number, name: string) => void;
}

/** Single-select category tree (left panel of the attribute-mapping page). */
export function CategoryTree({ categories, loading, selectedId, onSelect }: CategoryTreeProps) {
	const { t } = useTranslation();
	const [query, setQuery] = useState('');
	const [expanded, setExpanded] = useState<Set<number>>(new Set());

	const tree = useMemo(() => buildTree(categories), [categories]);

	const visibleTree = useMemo(() => {
		const q = query.trim().toLowerCase();
		return q ? filterTree(tree, q) : tree;
	}, [tree, query]);

	const flat = useMemo(() => {
		const out: FlatNode[] = [];
		flatten(visibleTree, expanded, query.trim() !== '', 0, out);
		return out;
	}, [visibleTree, expanded, query]);

	const toggleExpand = (id: number) =>
		setExpanded(prev => {
			const next = new Set(prev);
			next.has(id) ? next.delete(id) : next.add(id);
			return next;
		});

	const expandAll = () => {
		const ids: number[] = [];
		collectParentIds(tree, ids);
		setExpanded(new Set(ids));
	};
	const collapseAll = () => setExpanded(new Set());

	return (
		<div className="flex h-full flex-col overflow-hidden rounded-lg border border-border bg-surface">
			<div className="flex flex-wrap items-center gap-2 border-b border-border-subtle px-4 py-3">
				<div className="relative min-w-[10rem] flex-1">
					<Search size={14} className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-foreground-subtle" />
					<Input value={query} onChange={e => setQuery(e.target.value)} placeholder={t('common.search')} className="pl-9" />
				</div>
				<Button type="button" variant="outline" size="sm" onClick={expandAll}>
					{t('attributes.map.expandAll')}
				</Button>
				<Button type="button" variant="outline" size="sm" onClick={collapseAll}>
					{t('attributes.map.collapseAll')}
				</Button>
			</div>

			<div className="min-h-0 flex-1 overflow-y-auto">
				{loading ? (
					<div className="flex flex-col gap-2 p-4">
						{Array.from({ length: 8 }).map((_, i) => (
							<Skeleton key={i} className="h-6 w-full" />
						))}
					</div>
				) : flat.length === 0 ? (
					<p className="px-4 py-6 text-center text-sm text-foreground-muted">{t('common.noResults')}</p>
				) : (
					<div className="flex flex-col">
						{flat.map(({ node, level }) => {
							const isSelected = selectedId === node.id;
							const hasChildren = node.children.length > 0;
							const isExpanded = expanded.has(node.id) || query.trim() !== '';
							return (
								<button
									key={node.id}
									type="button"
									onClick={() => onSelect(node.id, node.name)}
									className={cn(
										'flex items-center gap-2 border-b border-border-subtle py-1.5 pr-4 text-left transition-colors last:border-b-0',
										isSelected ? 'bg-brand-subtle font-medium' : 'hover:bg-elevated'
									)}
									style={{ paddingLeft: `calc(1rem + ${level} * 1.5rem)` }}>
									<span className="flex h-5 w-5 shrink-0 items-center justify-center">
										{hasChildren && (
											<span
												role="button"
												tabIndex={-1}
												onClick={e => {
													e.stopPropagation();
													toggleExpand(node.id);
												}}
												aria-label={isExpanded ? t('attributes.map.collapse') : t('attributes.map.expand')}
												className="flex h-5 w-5 items-center justify-center rounded text-foreground-subtle hover:bg-muted hover:text-foreground">
												<ChevronRight size={12} className={cn('transition-transform', isExpanded && 'rotate-90')} />
											</span>
										)}
									</span>
									<span className="min-w-0 flex-1 truncate text-sm text-foreground">{node.name}</span>
								</button>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
}
